import { inject, Injectable } from '@angular/core';

import { AppSettings } from '../../models';
import { DefaultPinConfiguration } from '../../types';
import { LogService } from '../log/log.service';
import { APP_CONFIG_PROVIDERS, ConfigProvider } from './config-provider';

@Injectable({
  providedIn: 'root',
})
export class ConfigSettingsService {
  private readonly configProviders: ConfigProvider[] = [...inject(APP_CONFIG_PROVIDERS)].sort(
    (cp1, cp2) => (cp1.loadOrder ?? 100) - (cp2.loadOrder ?? 100),
  );
  private _appSettings: AppSettings = ConfigSettingsService.getDefault();

  public static getDefault(): AppSettings {
    return {
      einreichungV3Activated: false,
      constants: {
        maximumFileSizeInMb: 20,
        maximumAntragSizeInMb: 100,
        maximumAntragFilesLen: 255,
        minimumSeiteSizeInByte: 6000,
        antragsSizeInByteBeforeAsking: 6000000,
      },
      isMaintenance: false,
      personalNumberLength: 8,
      supportedOAuthGrants: new Set(['password']),
      customizing: {
        allowPdfUpload: false,
        firstBelegMandatoryConfig: {
          isActive: false,
          mandatoryPages: 0,
        },
        hasUebersicht: false,
        hasBescheide: false,
        hasUnfinishedAntrage: false,
        ui: {
          app: {
            footer: {
              hasCustomBorder: false,
              hasCustomBackground: false,
            },
          },
        },
      },
      connection: {
        auth: '',
        res: '',
        clientId: '',
      },
      pinConfiguration: DefaultPinConfiguration,
      showDataPrivacyPage: false,
    };
  }

  public get appSettings(): AppSettings {
    return this._appSettings;
  }

  public async loadConfigProviders(): Promise<void> {
    let appSettings = ConfigSettingsService.getDefault();
    for (const configProvider of this.configProviders) {
      const snapshot = ConfigSettingsService.cloneSettings(appSettings);
      try {
        appSettings = await configProvider.getAppSettings(snapshot);
      } catch (error: unknown) {
        try {
          const message = error instanceof Error ? error.message : String(error);
          LogService.error(`Config provider (loadOrder=${configProvider.loadOrder}) failed: ${message}`);
        } catch {
          throw new Error('Config loading halted: error logging failed');
        }
      }
    }
    this._appSettings = appSettings;
  }

  private static cloneSettings(settings: AppSettings): AppSettings {
    return {
      ...settings,
      supportedOAuthGrants: new Set(settings.supportedOAuthGrants),
      customizing: { ...settings.customizing },
      connection: { ...settings.connection },
      constants: { ...settings.constants },
      pinConfiguration: { ...settings.pinConfiguration },
      dashboard: settings.dashboard ? { ...settings.dashboard } : undefined,
      request: settings.request ? { ...settings.request } : undefined,
      sentry: settings.sentry ? { ...settings.sentry } : undefined,
      tutorial: settings.tutorial ? [...settings.tutorial] : undefined,
    };
  }
}
